<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import CaptainInboxesAPI from 'dashboard/api/captain/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const isSaving = ref(false);
const hasCaptain = ref(false);

const commentReplyEnabled = ref(false);
const commentReplyMessage = ref('');
const commentPublicReplyEnabled = ref(false);
const commentPublicReplyMessage = ref('');
const followerWelcomeEnabled = ref(false);
const followerWelcomeMessage = ref('');

async function checkCaptainStatus() {
  try {
    const { data } = await CaptainAssistantAPI.get();
    const assistants = data?.payload || data || [];
    for (const assistant of assistants) {
      try {
        const res = await CaptainInboxesAPI.get({
          assistantId: assistant.id,
        });
        const inboxes = res.data?.payload || res.data || [];
        if (
          inboxes.some(
            ci =>
              ci.inbox_id === props.inbox.id ||
              ci.inbox?.id === props.inbox.id
          )
        ) {
          hasCaptain.value = true;
          return;
        }
      } catch {
        // skip this assistant
      }
    }
    hasCaptain.value = false;
  } catch {
    hasCaptain.value = false;
  }
}

async function fetchConfig() {
  isLoading.value = true;
  try {
    const { data } = await InboxesAPI.getInstagramAutoReplyConfig(
      props.inbox.id
    );
    if (data.success && data.config) {
      const config = data.config;
      commentReplyEnabled.value = config.comment_reply?.enabled || false;
      commentReplyMessage.value = config.comment_reply?.message || '';
      commentPublicReplyEnabled.value =
        config.comment_public_reply?.enabled || false;
      commentPublicReplyMessage.value =
        config.comment_public_reply?.message || '';
      followerWelcomeEnabled.value = config.follower_welcome?.enabled || false;
      followerWelcomeMessage.value = config.follower_welcome?.message || '';
    }
  } catch {
    useAlert(t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
}

async function saveConfig() {
  isSaving.value = true;
  try {
    const config = {
      comment_reply: {
        enabled: commentReplyEnabled.value,
        message: commentReplyMessage.value,
      },
      comment_public_reply: {
        enabled: commentPublicReplyEnabled.value,
        message: commentPublicReplyMessage.value,
      },
      follower_welcome: {
        enabled: followerWelcomeEnabled.value,
        message: followerWelcomeMessage.value,
      },
    };
    const { data } = await InboxesAPI.updateInstagramAutoReplyConfig(
      props.inbox.id,
      config
    );
    if (data.success) {
      useAlert(t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SAVE_SUCCESS'));
    } else {
      useAlert(data.error || t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SAVE_ERROR'));
    }
  } catch {
    useAlert(t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

onMounted(() => {
  fetchConfig();
  checkCaptainStatus();
});
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.TITLE')"
      :sub-title="$t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SUBTITLE')"
    >
      <div v-if="isLoading" class="p-4 text-center">
        <span>{{ $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.LOADING') }}</span>
      </div>

      <div v-else class="space-y-8">
        <!-- Comment Auto-Reply DM Section -->
        <div class="space-y-4">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{
              $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.DESCRIPTION')
            }}
          </p>
          <div
            v-if="hasCaptain"
            class="p-3 rounded-lg bg-woot-50 dark:bg-woot-800/20 text-sm text-woot-600 dark:text-woot-300 border border-woot-200 dark:border-woot-700"
          >
            {{
              $t(
                'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.CAPTAIN_ACTIVE'
              )
            }}
          </div>
          <div class="flex items-center gap-3">
            <input
              id="comment-reply-enabled"
              v-model="commentReplyEnabled"
              type="checkbox"
              class="w-4 h-4"
            />
            <label for="comment-reply-enabled" class="font-medium">
              {{
                $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.ENABLE_LABEL')
              }}
            </label>
          </div>
          <div v-if="commentReplyEnabled">
            <label class="block mb-2 text-sm font-medium">
              {{
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.MESSAGE_LABEL'
                )
              }}
            </label>
            <textarea
              v-model="commentReplyMessage"
              class="w-full min-h-[80px] p-2 border border-n-weak rounded-lg text-sm"
              :placeholder="
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.MESSAGE_PLACEHOLDER'
                )
              "
              rows="3"
            />
          </div>
        </div>

        <!-- Comment Public Reply Section -->
        <div class="space-y-4">
          <h3 class="text-base font-medium text-n-slate-12">
            {{
              $t(
                'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_PUBLIC_REPLY.TITLE'
              )
            }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_PUBLIC_REPLY.DESCRIPTION'
              )
            }}
          </p>
          <div
            v-if="hasCaptain"
            class="p-3 rounded-lg bg-woot-50 dark:bg-woot-800/20 text-sm text-woot-600 dark:text-woot-300 border border-woot-200 dark:border-woot-700"
          >
            {{
              $t(
                'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_PUBLIC_REPLY.CAPTAIN_ACTIVE'
              )
            }}
          </div>
          <div class="flex items-center gap-3">
            <input
              id="comment-public-reply-enabled"
              v-model="commentPublicReplyEnabled"
              type="checkbox"
              class="w-4 h-4"
            />
            <label for="comment-public-reply-enabled" class="font-medium">
              {{
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_PUBLIC_REPLY.ENABLE_LABEL'
                )
              }}
            </label>
          </div>
          <div v-if="commentPublicReplyEnabled">
            <label class="block mb-2 text-sm font-medium">
              {{
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_PUBLIC_REPLY.MESSAGE_LABEL'
                )
              }}
            </label>
            <textarea
              v-model="commentPublicReplyMessage"
              class="w-full min-h-[80px] p-2 border border-n-weak rounded-lg text-sm"
              :placeholder="
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_PUBLIC_REPLY.MESSAGE_PLACEHOLDER'
                )
              "
              rows="3"
            />
          </div>
        </div>

        <!-- Follower Welcome Section -->
        <div class="space-y-4">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{
              $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.DESCRIPTION')
            }}
          </p>
          <div class="flex items-center gap-3">
            <input
              id="follower-welcome-enabled"
              v-model="followerWelcomeEnabled"
              type="checkbox"
              class="w-4 h-4"
            />
            <label for="follower-welcome-enabled" class="font-medium">
              {{
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.ENABLE_LABEL'
                )
              }}
            </label>
          </div>
          <div v-if="followerWelcomeEnabled">
            <label class="block mb-2 text-sm font-medium">
              {{
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.MESSAGE_LABEL'
                )
              }}
            </label>
            <textarea
              v-model="followerWelcomeMessage"
              class="w-full min-h-[80px] p-2 border border-n-weak rounded-lg text-sm"
              :placeholder="
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.MESSAGE_PLACEHOLDER'
                )
              "
              rows="3"
            />
          </div>
        </div>

        <!-- Save Button -->
        <div class="flex justify-end pt-4">
          <NextButton
            :is-loading="isSaving"
            :label="$t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SAVE')"
            @click="saveConfig"
          />
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
